// src/oura/components/newscan/QuestionEditor.tsx
//
// The in-place editor under a question row: the wording in a 2-row field,
// the checks that can run here (live, as staff type), the server's sentence
// when it refuses a save, what the question stays (its number and type) and
// the words it is built from (each one opens the Words view), and the ways
// out: Try another (up to 3 other wordings, each with why, then Use this),
// Recipe wording (a reworded question goes back to the recipe), Lock wording
// or Unlock (the row only shows a small lock once locked), Cancel, Save.
// While the other wordings are open, "Use this" is the one blue button.
//
// Enter (or Cmd/Ctrl+Enter) saves, Shift+Enter is left alone, Esc cancels;
// a line under the field says so. The wording opens selected, so typing
// replaces it. Lock and unlock write at once and close the editor (the foot
// line says "Question 4 locked"); with edited text the lock waits for Save,
// which locks the row anyway ("Locked on save"). Unsaved text survives the editor being
// closed by something else (a view switch, another row opened); Cancel and a
// successful save throw it away.
//
// A brand row (v4.5) says what it stays in plain words and where it came from:
// "Stays question 7, Recommend you, for the US. From Google: “...”."

import { CircleAlertIcon, LockIcon, LockOpenIcon, ShuffleIcon, Undo2Icon } from "lucide-react";
import { useEffect, useId, useRef, useState, type KeyboardEvent } from "react";
import type { ScanDraftAlternative, ScanDraftQuestion, ScanDraftWord } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { DrawerPulse, FOCUS_RING, PillButton, SURFACE, TYPE } from "@oura/ui";
import { RadioList } from "@oura/components/docs/create/RadioList";
import { textareaClass } from "@oura/onboarding/messageText";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { FRAME } from "@oura/newscan/copy";
import { Muted } from "./Muted";
import { BRAND_GROUPS, brandGroupOf, checkOf, isBrandCheck, marketName, marketOf } from "./brandReview";
import { containsPhrase } from "@oura/newscan/containsPhrase";

const MIN = 8;
const MAX = 220;

const COPY = {
    label: (n: number) => `Question ${n} wording`,
    empty: "Type the question.",
    short: `Too short. ${MIN} characters at least.`,
    long: (len: number) => `Too long: ${len} characters. ${MAX} at most.`,
    business: (name: string) => `Leave out ${name}. The engines have to name it on their own.`,
    place: (place: string) => `Name ${place}. This question asks about it.`,
    staysBrand: (n: number, group: string, market: string) => `Stays question ${n}, ${group}, for ${market}.`,
    fromGoogle: (search: string) => `From Google: “${search}”.`,
    untraced: "No Google search behind it.",
    builtFrom: "Built from",
    tryAnother: "Try another",
    recipe: "Recipe wording",
    recipeWas: (text: string) => `Recipe wording: “${text}”`,
    lock: "Lock wording",
    unlock: "Unlock",
    lockOnSave: "Locked on save",
    keys: "Enter saves · Esc cancels",
    cancel: "Cancel",
    save: "Save",
    altLabel: (n: number) => `Other wordings for question ${n}`,
    altLoading: "Finding other wordings",
    altNone: "No other wording fits this question.",
    use: "Use this",
} as const;

/** A word the question is built from, as a link to the Words view: 13px blue text with a 44px target. */
const WORD_LINK = cn(
    "relative rounded font-medium text-blue-600 hover:underline after:absolute after:-inset-x-0.5 after:-inset-y-3 after:content-['']",
    FOCUS_RING,
);

/** A refusal from the server or a failed read, where the staffer is looking. */
function Alert({ children }: { children: string }) {
    return (
        <p role="alert" className={cn(TYPE.tc, "m-0 break-words font-medium text-rose-600")}>
            {children}
        </p>
    );
}

/** Unsaved wording per draft and question, kept while the editor is closed by something else. */
const unsaved = new Map<string, string>();

/** The checks that need no server: empty, length, the business's own name, the outlet a location question asks about. */
function clientChecks(text: string, q: ScanDraftQuestion, businessName: string): string[] {
    const t = text.trim();
    if (!t) return [COPY.empty];
    const out: string[] = [];
    if (t.length < MIN) out.push(COPY.short);
    if (t.length > MAX) out.push(COPY.long(t.length));
    const name = businessName.trim();
    // Whole words only: a business called "Spa" does not block "spaces" (Jack, #4287).
    if (name.length >= 3 && containsPhrase(t, name)) out.push(COPY.business(name));
    if (q.check === "location" && q.fill) {
        // The outlet, or its own building, station or landmark, as the recipe allows.
        const places = [
            q.fill,
            ...Object.entries(q.fill_values)
                .filter(([v]) => v.startsWith("area"))
                .map(([, value]) => value),
        ].filter(Boolean);
        if (!places.some((p) => containsPhrase(t, p))) out.push(COPY.place(q.fill));
    }
    return out;
}

export interface QuestionEditorProps {
    question: ScanDraftQuestion;
    words: Record<string, ScanDraftWord>;
    id: string;
    /** Closes the editor; `saved` when a write landed (focus goes back to the row). */
    onClose(saved: boolean): void;
    /** Opens the Words view at this word. */
    onOpenWord(variable: string): void;
    /** Opened from the row menu's "Try another wording": the alternatives load at once. */
    autoTry?: boolean;
}

type Pending = "save" | "reset" | "use" | "lock" | null;

export function QuestionEditor({ question: q, words, id, onClose, onOpenWord, autoTry = false }: QuestionEditorProps) {
    const { ctl } = useScanFlow();
    const draft = ctl.draft;
    const key = `${draft?.id ?? ""}:${q.n}`;
    const [text, setText] = useState(() => unsaved.get(key) ?? q.text);
    const [serverError, setServerError] = useState<string | null>(null);
    const [pending, setPending] = useState<Pending>(null);
    const [alts, setAlts] = useState<ScanDraftAlternative[] | "loading" | null>(null);
    const [altError, setAltError] = useState<string | null>(null);
    const [picked, setPicked] = useState<string | null>(null);
    const fieldRef = useRef<HTMLTextAreaElement>(null);
    const rootRef = useRef<HTMLDivElement>(null);
    const altNoneRef = useRef<HTMLParagraphElement>(null);
    const fieldId = useId();
    const noteId = useId();
    const altLabelId = useId();
    const recipeWasId = useId();

    const businessName = draft?.business.businessName ?? "";
    const changed = text.trim() !== q.text.trim();
    const issues = changed ? clientChecks(text, q, businessName) : [];
    const busy = Boolean(ctl.busy) || pending !== null;

    // Into the field on open with the wording selected, so typing replaces it; the row stays where it is.
    useEffect(() => {
        const el = fieldRef.current;
        if (!el) return;
        el.focus({ preventScroll: true });
        el.select();
        el.scrollIntoView({ block: "nearest" });
    }, []);

    useEffect(() => {
        if (changed) unsaved.set(key, text);
        else unsaved.delete(key);
    }, [key, text, changed]);

    const failSentence = (kind: string, message: string) => (kind === "network" ? FRAME.network : message);

    const cancel = () => {
        if (pending) return;
        unsaved.delete(key);
        onClose(false);
    };

    const save = async () => {
        if (busy) return;
        if (!changed) {
            unsaved.delete(key);
            onClose(false);
            return;
        }
        if (issues.length) {
            fieldRef.current?.focus();
            return;
        }
        setPending("save");
        setServerError(null);
        const fail = await ctl.patchQuestion(q.n, { text: text.trim() });
        setPending(null);
        if (fail) {
            if (fail.kind !== "stale") setServerError(failSentence(fail.kind, fail.message));
            fieldRef.current?.focus();
            return;
        }
        unsaved.delete(key);
        onClose(true);
    };

    const reset = async () => {
        if (busy) return;
        setPending("reset");
        setServerError(null);
        const fail = await ctl.patchQuestion(q.n, { reset: true });
        setPending(null);
        if (fail) {
            if (fail.kind !== "stale") setServerError(failSentence(fail.kind, fail.message));
            return;
        }
        unsaved.delete(key);
        onClose(true);
    };

    /** A busy pill is disabled, so the button pressed loses focus; put it back where the staffer is. */
    const own = (action: string) => rootRef.current?.querySelector<HTMLElement>(`[data-q-action="${action}"]`);
    const refocus = (target: () => HTMLElement | null | undefined) =>
        requestAnimationFrame(() => {
            const a = document.activeElement;
            if (!a || a === document.body) target()?.focus({ preventScroll: true });
        });

    const toggleLock = async () => {
        if (busy) return;
        // Edited text waits for Save, which locks the row anyway.
        if (changed) return;
        setPending("lock");
        setServerError(null);
        const fail = await ctl.patchQuestion(q.n, { locked: !q.locked });
        setPending(null);
        if (fail) {
            if (fail.kind !== "stale") setServerError(failSentence(fail.kind, fail.message));
            refocus(() => own("lock"));
            return;
        }
        // Nothing left to save: close; the foot line says "Question 4 locked".
        unsaved.delete(key);
        onClose(true);
    };

    const tryAnother = async () => {
        if (alts === "loading") return;
        if (alts !== null) {
            setAlts(null);
            setPicked(null);
            return;
        }
        setAlts("loading");
        setAltError(null);
        const res = await ctl.alternatives(q.n);
        if (!res.ok) {
            setAlts(null);
            setAltError(failSentence(res.kind, res.message));
            refocus(() => own("try"));
            return;
        }
        setAlts(res.data.slice(0, 3));
        setPicked(res.data[0]?.id ?? null);
        // Into the list: the checked wording, or the line that says none fits.
        refocus(
            () =>
                rootRef.current?.querySelector<HTMLElement>('[role="radiogroup"] [role="radio"][aria-checked="true"]') ??
                altNoneRef.current ??
                own("try"),
        );
    };

    // The row menu's "Try another wording": once, when the editor opens.
    const tried = useRef(false);
    useEffect(() => {
        if (!autoTry || tried.current) return;
        tried.current = true;
        void tryAnother();
        // Only on open: tryAnother reads this render's state.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTry]);

    const acceptAlt = async () => {
        if (!picked || busy) return;
        setPending("use");
        setServerError(null);
        const fail = await ctl.acceptAlternative(q.n, picked);
        setPending(null);
        if (fail) {
            if (fail.kind !== "stale") setServerError(failSentence(fail.kind, fail.message));
            return;
        }
        unsaved.delete(key);
        onClose(true);
    };

    const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === "Escape") {
            e.preventDefault();
            e.stopPropagation();
            cancel();
            return;
        }
        if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
        // A question is one line: plain Enter in the field saves; Cmd/Ctrl+Enter saves from anywhere in the editor.
        const inField = e.target === fieldRef.current;
        if (e.metaKey || e.ctrlKey || (inField && !e.shiftKey && !e.altKey)) {
            e.preventDefault();
            void save();
        }
    };

    const builtFrom = Array.from(new Set(q.fill_vars)).filter((v) => words[v]);
    const brand = isBrandCheck(checkOf(q));
    const stays = brand
        ? [
              COPY.staysBrand(
                  q.n,
                  BRAND_GROUPS.find((g) => g.id === brandGroupOf(q))?.short ?? "",
                  marketName(marketOf(q), draft?.business).full,
              ),
              q.trace === undefined ? null : q.trace ? COPY.fromGoogle(q.trace.suggestion) : COPY.untraced,
          ]
              .filter(Boolean)
              .join(" ")
        : // The number and type a local row keeps went unread (Jovi, 20 Oct): only where it came from is said.
          null;

    return (
        <div ref={rootRef} id={id} className={cn(SURFACE.inner, "space-y-2.5 py-3")} onKeyDown={onKeyDown}>
            <label htmlFor={fieldId} className="sr-only">
                {COPY.label(q.n)}
            </label>
            <textarea
                ref={fieldRef}
                id={fieldId}
                rows={2}
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    if (serverError) setServerError(null);
                }}
                aria-invalid={issues.length > 0 || Boolean(serverError)}
                aria-describedby={noteId}
                aria-keyshortcuts="Enter Escape"
                autoComplete="off"
                spellCheck
                className={cn(textareaClass(issues.length || serverError ? "invalid" : null), "block resize-y")}
            />

            <div id={noteId} className="space-y-1.5">
                {issues.length > 0 && (
                    <ul role="list" className="list-plain m-0 space-y-1 p-0">
                        {issues.map((m) => (
                            <li key={m} className={cn(TYPE.tc, "flex items-start gap-1.5 font-medium text-rose-600")}>
                                <CircleAlertIcon aria-hidden size={14} className="mt-0.5 shrink-0" />
                                <span className="min-w-0 break-words">{m}</span>
                            </li>
                        ))}
                    </ul>
                )}
                {/* Keys mean nothing on a touch screen with no keyboard: shown only where a pointer is fine. */}
                <Muted as="p" size="tm" className="[@media(pointer:coarse)]:hidden">
                    {COPY.keys}
                </Muted>
                {serverError && <Alert>{serverError}</Alert>}
                {(stays || builtFrom.length > 0) && (
                <p className={cn(TYPE.tc, "m-0 break-words text-gray-500")}>
                    {stays}
                    {builtFrom.length > 0 && (
                        <>
                            {stays ? " " : ""}
                            {COPY.builtFrom}{" "}
                            {builtFrom.map((v, i) => (
                                <span key={v}>
                                    <button type="button" onClick={() => onOpenWord(v)} className={WORD_LINK}>
                                        {words[v].label.toLowerCase()}
                                    </button>
                                    {i < builtFrom.length - 2 ? ", " : i === builtFrom.length - 2 ? " and " : "."}
                                </span>
                            ))}
                        </>
                    )}
                </p>
                )}
                {q.reworded && q.original_text && (
                    <p id={recipeWasId} className={cn(TYPE.tc, "m-0 break-words text-gray-500")}>
                        {COPY.recipeWas(q.original_text)}
                    </p>
                )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
                <PillButton
                    data-q-action="try"
                    size="sm"
                    icon={ShuffleIcon}
                    aria-expanded={alts !== null}
                    busy={alts === "loading"}
                    disabled={busy}
                    onClick={() => void tryAnother()}
                    className="max-sm:h-9"
                >
                    {COPY.tryAnother}
                </PillButton>
                {q.reworded && q.original_text && (
                    <PillButton
                        size="sm"
                        icon={Undo2Icon}
                        busy={pending === "reset"}
                        disabled={busy && pending !== "reset"}
                        onClick={() => void reset()}
                        aria-describedby={recipeWasId}
                        className="max-sm:h-9"
                    >
                        {COPY.recipe}
                    </PillButton>
                )}
                <PillButton
                    data-q-action="lock"
                    size="sm"
                    icon={q.locked ? LockOpenIcon : LockIcon}
                    busy={pending === "lock"}
                    disabled={changed || (busy && pending !== "lock")}
                    onClick={() => void toggleLock()}
                    className="max-sm:h-9"
                >
                    {changed ? COPY.lockOnSave : q.locked ? COPY.unlock : COPY.lock}
                </PillButton>
                <span className="ml-auto flex items-center gap-2">
                    <PillButton size="sm" disabled={pending !== null} onClick={cancel} className="max-sm:h-9">
                        {COPY.cancel}
                    </PillButton>
                    <PillButton
                        variant={alts !== null && alts !== "loading" && alts.length > 0 ? "secondary" : "primary"}
                        size="sm"
                        busy={pending === "save"}
                        disabled={(busy && pending !== "save") || issues.length > 0}
                        onClick={() => void save()}
                        aria-keyshortcuts="Enter"
                        className="max-sm:h-9"
                    >
                        {COPY.save}
                    </PillButton>
                </span>
            </div>

            {alts !== null && (
                <div className="rounded-xl bg-white px-1 pb-2 pt-1 ring-1 ring-inset ring-black/[0.04]">
                    <p id={altLabelId} className={cn(TYPE.lab, "m-0 px-3 pt-2")}>
                        {COPY.altLabel(q.n)}
                    </p>
                    {alts === "loading" ? (
                        <div className="px-3 pt-2">
                            <DrawerPulse lines={2} label={COPY.altLoading} />
                        </div>
                    ) : alts.length === 0 ? (
                        <p ref={altNoneRef} tabIndex={-1} className={cn(TYPE.tc, "m-0 px-3 pt-1.5 text-gray-500 outline-none")}>
                            {COPY.altNone}
                        </p>
                    ) : (
                        <>
                            <RadioList
                                labelId={altLabelId}
                                options={alts.map((a) => ({ key: a.id, label: a.text, sub: a.why }))}
                                value={picked}
                                onPick={setPicked}
                            />
                            <div className="px-3 pt-1">
                                <PillButton
                                    variant="primary"
                                    size="sm"
                                    busy={pending === "use"}
                                    disabled={!picked || (busy && pending !== "use")}
                                    onClick={() => void acceptAlt()}
                                    className="max-sm:h-9"
                                >
                                    {COPY.use}
                                </PillButton>
                            </div>
                        </>
                    )}
                </div>
            )}
            {altError && <Alert>{altError}</Alert>}
        </div>
    );
}
